import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { ScrollArea } from '../../ui/scroll-area';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../ui/table";
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
    Shield,
    AlertTriangle,
    Camera,
    Copy,
    Eye,
    Loader2,
    RefreshCw,
    Users,
    Monitor,
    Trash2,
    Search,
    X
} from 'lucide-react';

// Map violation types to user-friendly labels and icons
const VIOLATION_TYPES = {
    'screenshot_printscreen': { label: 'PrintScreen', icon: Camera, severity: 'high' },
    'screenshot_snipping_tool': { label: 'Snipping Tool', icon: Camera, severity: 'high' },
    'screenshot_mac': { label: 'Mac Screenshot', icon: Camera, severity: 'high' },
    'screenshot_attempt': { label: 'Browser Screenshot', icon: Camera, severity: 'high' },
    'copy_attempt': { label: 'Copy Text', icon: Copy, severity: 'medium' },
    'copy_keyboard': { label: 'Ctrl+C', icon: Copy, severity: 'medium' },
    'cut_attempt': { label: 'Cut Text', icon: Copy, severity: 'medium' },
    'cut_keyboard': { label: 'Ctrl+X', icon: Copy, severity: 'medium' },
    'right_click': { label: 'Right Click', icon: Copy, severity: 'low' },
    'print_attempt': { label: 'Print', icon: Monitor, severity: 'medium' },
    'devtools_attempt': { label: 'DevTools', icon: Eye, severity: 'medium' },
    'devtools_f12': { label: 'F12 Key', icon: Eye, severity: 'medium' },
    'devtools_console': { label: 'Console', icon: Eye, severity: 'medium' },
    'tab_hidden': { label: 'Tab Switch', icon: Monitor, severity: 'low' },
    'window_blur': { label: 'Window Blur', icon: Monitor, severity: 'low' },
    'focus_tab_switch': { label: 'Left Page: Tab', icon: Monitor, severity: 'medium' },
    'focus_window_blur': { label: 'Left Page: Window', icon: Monitor, severity: 'medium' },
    'focus_mouse_leave': { label: 'Left Page: Mouse', icon: Monitor, severity: 'medium' },
    'focus_signed_out': { label: 'Signed Out (Strikes)', icon: Eye, severity: 'high' },
    'paste_blocked': { label: 'Paste Blocked', icon: Copy, severity: 'medium' },
    'select_all_attempt': { label: 'Select All', icon: Copy, severity: 'low' },
    'drag_attempt': { label: 'Drag', icon: Copy, severity: 'low' },
    'save_attempt': { label: 'Save Page', icon: Monitor, severity: 'low' },
    'view_source_attempt': { label: 'View Source', icon: Eye, severity: 'low' }
};

const getSeverityColor = (severity) => {
    switch (severity) {
        case 'high': return 'bg-red-500/20 text-red-700 border-red-500/30';
        case 'medium': return 'bg-amber-500/20 text-amber-700 border-amber-500/30';
        default: return 'bg-slate-500/20 text-slate-700 border-slate-500/30';
    }
};

const ScreenshotViolationsTable = () => {
    const { api } = useAuth();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showUserModal, setShowUserModal] = useState(false);
    const [userViolations, setUserViolations] = useState(null);
    const [loadingUser, setLoadingUser] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [daysFilter, setDaysFilter] = useState('7');

    const fetchViolations = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('days', daysFilter);
            params.append('limit', '200');
            if (typeFilter !== 'all') {
                params.append('type', typeFilter);
            }
            
            const res = await api.get(`/content-protection/admin/violations?${params.toString()}`);
            setData(res.data);
        } catch (err) {
            console.error('Failed to fetch violations:', err);
            toast.error('Failed to load violations');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchViolations();
    }, [daysFilter, typeFilter]);

    const viewUserDetails = async (userId) => {
        setLoadingUser(true);
        setShowUserModal(true);
        try {
            const res = await api.get(`/content-protection/admin/user-violations/${userId}`);
            setUserViolations(res.data);
            setSelectedUser(res.data.user);
        } catch (err) {
            console.error('Failed to fetch user violations:', err);
            toast.error('Failed to load user details');
        } finally {
            setLoadingUser(false);
        }
    };

    const clearOldViolations = async () => {
        if (!window.confirm('This will permanently delete violations older than 30 days. Continue?')) {
            return;
        }
        try {
            const res = await api.delete('/content-protection/admin/violations/clear-old?days=30');
            toast.success(`Deleted ${res.data.deleted_count} old violation records`);
            fetchViolations();
        } catch (err) {
            toast.error('Failed to clear old violations');
        }
    };

    // Filter user stats by search term
    const filteredUserStats = data?.user_stats?.filter(u => 
        !searchTerm || 
        u.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    if (loading && !data) {
        return (
            <Card>
                <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card data-testid="screenshot-violations-panel">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-red-100 text-red-600">
                            <Shield className="h-5 w-5" />
                        </div>
                        <div>
                            <CardTitle className="text-lg">Content Protection Violations</CardTitle>
                            <CardDescription>
                                Track screenshot attempts, copy actions, and other violations
                            </CardDescription>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={fetchViolations}
                            disabled={loading}
                        >
                            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </Button>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={clearOldViolations}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Clear Old
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-red-600">{data?.total_violations || 0}</p>
                            <p className="text-xs text-muted-foreground">Total Violations</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{data?.unique_users || 0}</p>
                            <p className="text-xs text-muted-foreground">Users with Violations</p>
                        </CardContent>
                    </Card>
                    <Card className="border-red-200 bg-red-50">
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-red-600">
                                {data?.type_breakdown?.filter(t => 
                                    VIOLATION_TYPES[t._id]?.severity === 'high' ||
                                    t._id?.toLowerCase().includes('screenshot') ||
                                    t._id?.toLowerCase().includes('snip')
                                ).reduce((sum, t) => sum + t.count, 0) || 0}
                            </p>
                            <p className="text-xs text-red-700">Screenshot Attempts</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold">{daysFilter}</p>
                            <p className="text-xs text-muted-foreground">Days Period</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search by user email or name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                        />
                        {searchTerm && (
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                                onClick={() => setSearchTerm('')}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Violation Type" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            {Object.entries(VIOLATION_TYPES).map(([key, info]) => (
                                <SelectItem key={key} value={key}>{info.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={daysFilter} onValueChange={setDaysFilter}>
                        <SelectTrigger className="w-[140px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="1">Last 24 Hours</SelectItem>
                            <SelectItem value="7">Last 7 Days</SelectItem>
                            <SelectItem value="30">Last 30 Days</SelectItem>
                            <SelectItem value="90">Last 90 Days</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Violation Type Breakdown */}
                {data?.type_breakdown?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {data.type_breakdown.map(t => {
                            const info = VIOLATION_TYPES[t._id] || { label: t._id, severity: 'low' };
                            return (
                                <Badge 
                                    key={t._id} 
                                    variant="outline" 
                                    className={getSeverityColor(info.severity)}
                                >
                                    {info.label}: {t.count}
                                </Badge>
                            );
                        })}
                    </div>
                )}

                {/* Users Table */}
                <div>
                    <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Users with Violations ({filteredUserStats.length})
                    </h3>
                    <Card>
                        <CardContent className="p-0">
                            <ScrollArea className="h-[400px]">
                                <Table>
                                    <TableHeader className="sticky top-0 bg-background">
                                        <TableRow>
                                            <TableHead>User</TableHead>
                                            <TableHead>Violations</TableHead>
                                            <TableHead>Types</TableHead>
                                            <TableHead>Last Violation</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredUserStats.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                                    {searchTerm ? 'No users match your search' : 'No violations recorded'}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filteredUserStats.map((user) => {
                                                const hasHighSeverity = user.violation_types?.some(t => 
                                                    VIOLATION_TYPES[t]?.severity === 'high'
                                                );
                                                
                                                return (
                                                    <TableRow 
                                                        key={user._id}
                                                        className={hasHighSeverity ? 'bg-red-50/50' : ''}
                                                    >
                                                        <TableCell>
                                                            <div>
                                                                <p className="font-medium">{user.user_name || 'Unknown'}</p>
                                                                <p className="text-xs text-muted-foreground">{user.user_email}</p>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge 
                                                                variant="outline" 
                                                                className={user.total_violations > 10 
                                                                    ? 'bg-red-100 text-red-700 border-red-300' 
                                                                    : user.total_violations > 5 
                                                                        ? 'bg-amber-100 text-amber-700 border-amber-300'
                                                                        : ''
                                                                }
                                                            >
                                                                {user.total_violations}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex flex-wrap gap-1">
                                                                {user.violation_types?.slice(0, 3).map(type => {
                                                                    const info = VIOLATION_TYPES[type] || { label: type, severity: 'low' };
                                                                    const Icon = info.icon || AlertTriangle;
                                                                    return (
                                                                        <Badge 
                                                                            key={type} 
                                                                            variant="outline" 
                                                                            className={`text-xs ${getSeverityColor(info.severity)}`}
                                                                        >
                                                                            <Icon className="h-3 w-3 mr-1" />
                                                                            {info.label}
                                                                        </Badge>
                                                                    );
                                                                })}
                                                                {user.violation_types?.length > 3 && (
                                                                    <Badge variant="outline" className="text-xs">
                                                                        +{user.violation_types.length - 3}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <span className="text-sm text-muted-foreground">
                                                                {user.last_violation 
                                                                    ? formatDistanceToNow(new Date(user.last_violation), { addSuffix: true })
                                                                    : '-'
                                                                }
                                                            </span>
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <Button 
                                                                variant="ghost" 
                                                                size="sm"
                                                                onClick={() => viewUserDetails(user._id)}
                                                            >
                                                                <Eye className="h-4 w-4" />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </ScrollArea>
                        </CardContent>
                    </Card>
                </div>
            </CardContent>

            {/* User Detail Modal */}
            <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Shield className="h-5 w-5 text-red-500" />
                            Violation Details: {selectedUser?.name || selectedUser?.email || 'User'}
                        </DialogTitle>
                        <DialogDescription>
                            {selectedUser?.email}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {loadingUser ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : userViolations ? (
                        <div className="flex-1 overflow-hidden flex flex-col gap-4">
                            {/* Type breakdown */}
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(userViolations.type_breakdown || {}).map(([type, count]) => {
                                    const info = VIOLATION_TYPES[type] || { label: type, severity: 'low' };
                                    return (
                                        <Badge 
                                            key={type} 
                                            variant="outline" 
                                            className={getSeverityColor(info.severity)}
                                        >
                                            {info.label}: {count}
                                        </Badge>
                                    );
                                })}
                            </div>

                            {/* Violations list */}
                            <ScrollArea className="flex-1 border rounded-lg">
                                <div className="divide-y">
                                    {userViolations.violations?.map((v, idx) => {
                                        const info = VIOLATION_TYPES[v.type] || { label: v.type, severity: 'low' };
                                        const Icon = info.icon || AlertTriangle;
                                        return (
                                            <div key={idx} className="p-3 hover:bg-muted/50">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <Icon className={`h-4 w-4 ${
                                                            info.severity === 'high' ? 'text-red-500' :
                                                            info.severity === 'medium' ? 'text-amber-500' : 'text-slate-500'
                                                        }`} />
                                                        <span className="font-medium">{info.label}</span>
                                                        {v.key && (
                                                            <Badge variant="outline" className="text-xs">
                                                                {v.key}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <span className="text-xs text-muted-foreground">
                                                        {v.recorded_at 
                                                            ? format(new Date(v.recorded_at), 'MMM d, yyyy HH:mm:ss')
                                                            : '-'
                                                        }
                                                    </span>
                                                </div>
                                                {v.page && (
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        Page: {v.page}
                                                    </p>
                                                )}
                                                {v.reason && (
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        {v.reason}
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </ScrollArea>
                        </div>
                    ) : (
                        <p className="text-center text-muted-foreground py-8">
                            No violation details available
                        </p>
                    )}
                </DialogContent>
            </Dialog>
        </Card>
    );
};

export default ScreenshotViolationsTable;
